import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText
} from '@mui/material';
import { LoadingOrb } from './LoadingOrb';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

import { patientService } from '../services/patientService';
import { userService, User } from '../services/userService';
import { Patient, PatientFormData, PatientValidationErrors, GENDER_OPTIONS } from '../types/patient';
import { useAuth } from '../contexts/AuthContext';

interface PatientFormProps {
  patient?: Patient;
  onSubmit: (data: PatientFormData) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const PatientForm: React.FC<PatientFormProps> = ({
  patient,
  onSubmit,
  onCancel,
  isEditing = false
}) => {
  const { user: currentUser } = useAuth();
  const [evaluators, setEvaluators] = useState<User[]>([]);
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'M',
    identificationNumber: '',
    phone: '',
    email: '',
    address: '',
    evaluatorId: currentUser?.id || '' // Autoseleccionar el usuario actual
  });

  const [errors, setErrors] = useState<PatientValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Valor visual del DatePicker, desacoplado de formData.dateOfBirth. Si el picker
  // quedara atado directamente a formData (que solo se actualiza con fechas completas
  // y válidas), MUI pierde sincronía con lo que el usuario está tecleando a mitad de
  // una fecha y termina reseteando día/mes/año a mitad de la escritura del año.
  const [dobPickerValue, setDobPickerValue] = useState<Date | null>(
    patient?.dateOfBirth ? new Date(patient.dateOfBirth + 'T12:00:00') : null
  );

  // Cargar evaluadores disponibles
  // Asegurar que el evaluatorId se establece cuando currentUser esté disponible
  useEffect(() => {
    if (!formData.evaluatorId && currentUser?.id) {
      setFormData(prev => ({ ...prev, evaluatorId: currentUser.id }));
    }
  }, [currentUser?.id]);

  // Cargar evaluadores disponibles
  useEffect(() => {
    const loadEvaluators = async () => {
      try {
        const evaluatorsList = await userService.getEvaluators();
        setEvaluators(evaluatorsList);
      } catch (error) {
        console.error('Error cargando evaluadores:', error);
      }
    };
    loadEvaluators();
  }, []);

  // Inicializar formulario con datos del paciente si se está editando
  useEffect(() => {
    if (patient) {
      setFormData({
        firstName: patient.firstName,
        lastName: patient.lastName,
        dateOfBirth: patient.dateOfBirth,
        gender: patient.gender,
        identificationNumber: patient.identificationNumber,
        phone: patient.phone || '',
        email: patient.email || '',
        address: patient.address || '',
        evaluatorId: patient.evaluatorId
      });
      setDobPickerValue(patient.dateOfBirth ? new Date(patient.dateOfBirth + 'T12:00:00') : null);
    }
  }, [patient]);

  // Función para capitalizar primera letra de cada palabra
  const capitalizeWords = (text: string): string => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Manejar cambios en los campos
  const handleChange = (field: keyof PatientFormData, value: any) => {
    let processedValue = value;
    
    // Capitalizar nombre y apellido automáticamente
    if (field === 'firstName' || field === 'lastName') {
      processedValue = capitalizeWords(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Marcar campo como tocado
  const handleBlur = (field: keyof PatientFormData) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };



  // Validar campo individual
  const validateField = (field: keyof PatientFormData, value: any): string | undefined => {
    switch (field) {
      case 'firstName':
        if (!value.trim()) return 'El nombre es obligatorio';
        if (value.length < 2) return 'El nombre debe tener al menos 2 caracteres';
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
          return 'El nombre solo puede contener letras y espacios';
        }
        break;

      case 'lastName':
        if (!value.trim()) return 'El apellido es obligatorio';
        if (value.length < 2) return 'El apellido debe tener al menos 2 caracteres';
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
          return 'El apellido solo puede contener letras y espacios';
        }
        break;

      case 'dateOfBirth':
        if (!value) return 'La fecha de nacimiento es obligatoria';
        if (!patientService.validateDateOfBirth(value)) {
          return 'La fecha de nacimiento no es válida (edad entre 3 y 120 años)';
        }
        break;

      case 'gender':
        if (!value) return 'El género es obligatorio';
        break;

      case 'identificationNumber':
        if (!value.trim()) return 'El RUT es obligatorio';
        if (!patientService.validateRut(value)) {
          return 'El RUT no es válido (formato: XX.XXX.XXX-X)';
        }
        break;

      case 'phone':
        if (value && !patientService.validatePhone(value)) {
          return 'El teléfono debe tener formato chileno: +56 9 XXXX XXXX';
        }
        break;

      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'El email no es válido';
        }
        break;

      case 'address':
        if (value && value.length > 500) {
          return 'La dirección no puede exceder 500 caracteres';
        }
        break;

      case 'evaluatorId':
        if (!value) return 'El evaluador es obligatorio';
        break;
    }
    return undefined;
  };

  // Validar formulario completo
  const validateForm = (): boolean => {
    const newErrors: PatientValidationErrors = {};
    
    Object.keys(formData).forEach((field) => {
      const fieldKey = field as keyof PatientFormData;
      const error = validateField(fieldKey, formData[fieldKey]);
      if (error) {
        newErrors[fieldKey] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  // Formatear RUT automáticamente
  const formatRut = (value: string) => {
    // Remover todo excepto números y k
    const clean = value.replace(/[^0-9kK]/g, '');

    if (clean.length <= 1) return clean;
    if (clean.length <= 4) return `${clean.slice(0, -3)}.${clean.slice(-3)}`;
    if (clean.length <= 7) return `${clean.slice(0, -6)}.${clean.slice(-6, -3)}.${clean.slice(-3)}`;

    return `${clean.slice(0, -7)}.${clean.slice(-7, -4)}.${clean.slice(-4, -1)}-${clean.slice(-1)}`;
  };

  // Manejar cambios en el RUT: formatea y valida en cuanto el RUT queda completo
  // (dígito verificador incluido), sin esperar al submit del formulario.
  const handleRutChange = (value: string) => {
    const clean = value.replace(/[^0-9kK]/g, '');
    const formatted = formatRut(value);

    handleChange('identificationNumber', formatted);

    const MIN_RUT_DIGITS = 8; // 7 dígitos + verificador (mínimo típico)
    if (clean.length >= MIN_RUT_DIGITS) {
      const error = validateField('identificationNumber', formatted);
      setErrors(prev => ({ ...prev, identificationNumber: error }));
      setTouched(prev => ({ ...prev, identificationNumber: true }));
    }
  };



  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Box display="flex" flexWrap="wrap" gap={3}>
        {/* Nombre */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="Nombre *"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            error={touched.firstName && !!errors.firstName}
            helperText={touched.firstName && errors.firstName}
            disabled={loading}
          />
        </Box>

        {/* Apellido */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="Apellido *"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            onBlur={() => handleBlur('lastName')}
            error={touched.lastName && !!errors.lastName}
            helperText={touched.lastName && errors.lastName}
            disabled={loading}
          />
        </Box>

        {/* Fecha de Nacimiento */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              label="Fecha de Nacimiento *"
              value={dobPickerValue}
              onChange={(date) => {
                // Reflejar siempre lo tecleado en el picker (incluye null mientras
                // se completan secciones) para que MUI no pierda sincronía con su
                // propio estado interno y resetee día/mes/año a mitad de escritura.
                setDobPickerValue(date);
                if (date && !isNaN(date.getTime())) {
                  // Usar UTC para evitar problemas de zona horaria
                  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                  const year = utcDate.getUTCFullYear();
                  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(utcDate.getUTCDate()).padStart(2, '0');
                  handleChange('dateOfBirth', `${year}-${month}-${day}`);
                }
              }}
              onAccept={(date) => {
                if (!date) {
                  handleChange('dateOfBirth', '');
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: touched.dateOfBirth && !!errors.dateOfBirth,
                  helperText: touched.dateOfBirth && errors.dateOfBirth,
                  disabled: loading,
                  onBlur: () => handleBlur('dateOfBirth'),
                  placeholder: "dd/mm/aaaa"
                }
              }}
              maxDate={new Date()}
              format="dd/MM/yyyy"
              closeOnSelect={true}
              disableOpenPicker={false}
              openTo="year"
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
        </Box>

        {/* Género */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <FormControl fullWidth error={touched.gender && !!errors.gender}>
            <InputLabel>Género *</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              onBlur={() => handleBlur('gender')}
              label="Género *"
              disabled={loading}
            >
              {GENDER_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {touched.gender && errors.gender && (
              <FormHelperText>{errors.gender}</FormHelperText>
            )}
          </FormControl>
        </Box>

        {/* RUT */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="RUT *"
            value={formData.identificationNumber}
            onChange={(e) => handleRutChange(e.target.value)}
            onBlur={() => handleBlur('identificationNumber')}
            error={touched.identificationNumber && !!errors.identificationNumber}
            helperText={touched.identificationNumber && errors.identificationNumber || 'Formato: XX.XXX.XXX-X'}
            disabled={loading}
            placeholder="12.345.678-9"
          />
        </Box>

        {/* Teléfono */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="Teléfono"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone || 'Ejemplo: +56912345678 o +56 9 1234 5678'}
            disabled={loading}
            placeholder="+56912345678"
            inputProps={{
              maxLength: 20
            }}
          />
        </Box>

        {/* Email */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
            disabled={loading}
            placeholder="ejemplo@email.com"
          />
        </Box>

        {/* Evaluador */}
        <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
          <FormControl fullWidth error={touched.evaluatorId && !!errors.evaluatorId}>
            <InputLabel>Evaluador *</InputLabel>
            <Select
              value={formData.evaluatorId}
              onChange={(e) => handleChange('evaluatorId', e.target.value)}
              onBlur={() => handleBlur('evaluatorId')}
              label="Evaluador *"
              disabled={loading}
            >
              {evaluators.map(evaluator => (
                <MenuItem key={evaluator.id} value={evaluator.id}>
                  {evaluator.firstName} {evaluator.lastName} ({evaluator.role})
                </MenuItem>
              ))}
            </Select>
            {touched.evaluatorId && errors.evaluatorId && (
              <FormHelperText>{errors.evaluatorId}</FormHelperText>
            )}
          </FormControl>
        </Box>

        {/* Dirección */}
        <Box sx={{ minWidth: '100%', flex: '1 1 100%' }}>
          <TextField
            fullWidth
            label="Dirección"
            multiline
            rows={3}
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            onBlur={() => handleBlur('address')}
            error={touched.address && !!errors.address}
            helperText={touched.address && errors.address}
            disabled={loading}
            placeholder="Av. Providencia 123, Santiago"
          />
        </Box>

        {/* Botones */}
        <Box sx={{ minWidth: '100%', flex: '1 1 100%' }}>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <LoadingOrb size={20} /> : undefined}
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PatientForm; 